<template>
  <div class="design-feedback card mt-0" v-if="visible">
    <div class="card-body">
      <h5 class="card-title fw-bold mb-3">
        <i class="bi bi-clipboard-check me-2" aria-hidden="true"></i>Design
        Feedback
      </h5>
      <div v-if="!submitted" class="text-muted text-center py-4">
        <p class="mb-0">Submit your design to receive feedback.</p>
      </div>
      <div v-else aria-live="polite">
        <!-- Summary cards -->
        <div class="row g-3 mb-3">
          <div class="col-md-3">
            <div
              class="border rounded p-3 text-center"
              :class="overBudget ? 'border-danger' : 'border-success'"
            >
              <div class="text-muted small">Total Cost</div>
              <div
                class="fs-5 fw-bold"
                :class="overBudget ? 'text-danger' : 'text-success'"
              >
                ${{ totalCost.toLocaleString() }}
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="border rounded p-3 text-center">
              <div class="text-muted small">Budget</div>
              <div class="fs-5 fw-bold">${{ budget.toLocaleString() }}</div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="border rounded p-3 text-center">
              <div class="text-muted small">Components</div>
              <div class="fs-5 fw-bold">{{ componentCount }}</div>
            </div>
          </div>
          <div class="col-md-3">
            <div
              class="border rounded p-3 text-center"
              :class="statusBorderClass"
            >
              <div class="text-muted small">Status</div>
              <div class="fs-5 fw-bold" :class="statusTextClass">
                {{ statusLabel }}
              </div>
            </div>
          </div>
        </div>

        <!-- Operating point data (when simulation ran successfully) -->
        <div v-if="simResult && simResult.success" class="row g-3 mb-3">
          <div class="col-md-3">
            <div class="border rounded p-3 text-center">
              <div class="text-muted small">Flow Rate</div>
              <div class="fs-6 fw-bold">
                {{ simResult.operatingPoint.Q_GPM.toFixed(1) }} GPM
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="border rounded p-3 text-center">
              <div class="text-muted small">Pump Head</div>
              <div class="fs-6 fw-bold">
                {{ simResult.operatingPoint.Hp_ft.toFixed(1) }} ft
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="border rounded p-3 text-center">
              <div class="text-muted small">Efficiency</div>
              <div class="fs-6 fw-bold">
                {{ efficiencyPercent(simResult.power.efficiency) }}%
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="border rounded p-3 text-center">
              <div class="text-muted small">Electrical Power</div>
              <div class="fs-6 fw-bold">
                {{
                  (
                    simResult.power.electrical_hp ??
                    wattsToHp(simResult.power.electrical_W)
                  ).toFixed(2)
                }}
                hp
              </div>
            </div>
          </div>
        </div>

        <!-- Validation messages -->
        <div v-if="feedbackMessages.length > 0">
          <div
            v-for="(msg, index) in feedbackMessages"
            :key="index"
            class="alert d-flex align-items-start mb-2"
            :class="'alert-' + msg.type"
            role="alert"
          >
            <i class="bi me-2 mt-1" :class="msg.icon" aria-hidden="true"></i>
            <div>{{ msg.text }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { W_TO_HP } from "@/utils/units.js";

const EFFICIENCY_PERCENT_SCALE = 100;

export default {
  name: "DesignFeedback",
  props: {
    submitted: {
      type: Boolean,
      default: false
    },
    visible: {
      type: Boolean,
      default: true
    },
    totalCost: {
      type: Number,
      default: 0
    },
    budget: {
      type: Number,
      default: 0
    },
    componentCount: {
      type: Number,
      default: 0
    },
    components: {
      type: Array,
      default: () => []
    },
    simResult: {
      type: Object,
      default: null
    },
    validationResult: {
      type: Object,
      default: null
    }
  },
  computed: {
    overBudget() {
      return this.totalCost > this.budget;
    },
    designPassed() {
      return this.validationResult ? this.validationResult.passed : false;
    },
    statusLabel() {
      if (!this.validationResult) {
        if (this.overBudget) return "Over Budget";
        if (this.componentCount === 0) return "Empty";
        return "Within Budget";
      }
      return this.designPassed ? "Passes" : "Fails";
    },
    statusBorderClass() {
      if (!this.validationResult) {
        if (this.overBudget) return "border-danger";
        if (this.componentCount === 0) return "border-warning";
        return "border-success";
      }
      return this.designPassed ? "border-success" : "border-danger";
    },
    statusTextClass() {
      if (!this.validationResult) {
        if (this.overBudget) return "text-danger";
        if (this.componentCount === 0) return "text-warning";
        return "text-success";
      }
      return this.designPassed ? "text-success" : "text-danger";
    },
    feedbackMessages() {
      if (!this.validationResult) return [];

      return this.validationResult.results.map((r) => {
        const typeMap = {
          hard: "danger",
          soft: "warning",
          success: "success"
        };
        const iconMap = {
          hard: "bi-exclamation-triangle-fill",
          soft: "bi-exclamation-circle-fill",
          success: "bi-check-circle-fill"
        };
        return {
          type: typeMap[r.severity] || "info",
          icon: iconMap[r.severity] || "bi-info-circle-fill",
          text: r.message
        };
      });
    }
  },
  methods: {
    efficiencyPercent(val) {
      return (val * EFFICIENCY_PERCENT_SCALE).toFixed(1);
    },
    wattsToHp(watts) {
      return watts * W_TO_HP;
    }
  }
};
</script>
